import { makeRequestWithErrorReporting } from 'infrastructure/api/request'
import Configs from 'infrastructure/config/environment'

function contentServiceUrl(path: string) {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  return `${Configs().ContentServiceUrl}/${cleanPath}`
}

/**
 * Make a GET request to the content service with error reporting.
 * Returns a result/error tuple.
 */
export function contentServiceGet<T>({
  path,
  optionOverrides = {},
}: {
  path: string
  optionOverrides?: RequestInit
}) {
  return makeRequestWithErrorReporting<T>({
    url: contentServiceUrl(path),
    method: 'GET',
    optionOverrides: {
      ...optionOverrides,
      headers: new Headers({
        Accept: 'application/json',
        ...(optionOverrides.headers ?? {}),
      }),
      credentials: 'include',
    },
  })
}

/**
 * Make a POST request to the content service with error reporting.
 * Returns a result/error tuple.
 */
export function contentServicePost<T>({
  path,
  body,
  optionOverrides = {},
}: {
  path: string
  body?: object
  optionOverrides?: RequestInit
}) {
  return makeRequestWithErrorReporting<T>({
    url: contentServiceUrl(path),
    method: 'POST',
    optionOverrides: {
      ...optionOverrides,
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(optionOverrides.headers ?? {}),
      }),
      body: body && JSON.stringify(body),
      credentials: 'include',
    },
  })
}

/**
 * Make a DELETE request to the content service with error reporting.
 * Returns a result/error tuple.
 */
export function contentServiceDelete<T>({
  path,
  optionOverrides = {},
}: {
  path: string
  optionOverrides?: RequestInit
}) {
  return makeRequestWithErrorReporting<T>({
    url: contentServiceUrl(path),
    method: 'DELETE',
    ...optionOverrides,
    optionOverrides: {
      headers: new Headers({
        Accept: 'application/json',
        ...(optionOverrides.headers ?? {}),
      }),
      credentials: 'include',
    },
  })
}

/**
 * Make a raw POST request to the content service.
 * Returns the fetch Response directly.
 */
export function makeContentServicePostRequest(
  path: string,
  body: object,
  queryParams: Record<string, string> = {},
) {
  const url = new URL(
    `${Configs().ContentServiceUrl}${path.startsWith('/') ? '' : '/'}${path}`,
  )
  Object.entries(queryParams).forEach(([key, value]) => {
    url.searchParams.append(key, value)
  })

  return fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  })
}

/**
 * Make a POST request to the content service for flow operations.
 * Returns an object with error array and/or flow_id result.
 */
export async function makeContentServiceFlowRequest(
  path: string,
  body: object,
) {
  try {
    const result = await makeContentServicePostRequest(path, body)
    if (!result.ok) {
      const json = (await result.json()) as {
        errors?: Array<{
          title: string
          status: string | number
          code?: string | number
          detail?: string
        }>
      }
      const errDetails =
        json.errors?.map(
          ({ title, status, code, detail }) =>
            `${title}: ${status}: ${code}: ${detail}`,
        ) ?? []
      return {
        error: [`${result.status}: ${result.statusText}`, ...errDetails],
      }
    }
    const json = (await result.json()) as {
      errors?: { code: string; detail: string; status: string; title: string }[]
      flow_id?: string
    }
    const errors = json.errors?.map(
      ({ title, status, code, detail }) =>
        `${title}: ${status}: ${code}: ${detail}`,
    )
    return { error: errors, result: json.flow_id }
  } catch (e: unknown) {
    return { error: [String(e)] }
  }
}

/**
 * Make a raw GET request to the content service.
 * Returns the fetch Response directly.
 */
export function makeContentServiceGetRequest(path: string) {
  return fetch(`${Configs().ContentServiceUrl}/${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
}

/**
 * Make a raw DELETE request to the content service.
 * Returns the fetch Response directly.
 */
export function makeContentServiceDeleteRequest(path: string) {
  return fetch(`${Configs().ContentServiceUrl}/${path}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
}
